<script>
import OrganizationEventsFilters from '@organizations/components/events-organization-filters.vue'
import EventsParentMixin from '@events/mixins/events-parent-mixin'
import Layout from '@layouts/loggedin.vue'
import store from '@state/store'
import { authComputed } from '@state/helpers'
import { debounce } from 'debounce'
import { parseISO } from 'date-fns'
import { isFuture, isPast } from 'date-fns/esm'
import { convertToCSV } from '@events/utils'

export default {
  name: 'OrganizationEvents',
  components: { Layout, OrganizationEventsFilters },
  mixins: [EventsParentMixin],
  props: {
    organizationId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      currentDate: new Date(),
      eventsPage: 0,
      eventsPerPage: 12,
      singleEvent: false,
    }
  },
  computed: {
    organization() {
      return this.$store.state.organizations.organization || {}
    },
    allEvents() {
      const events = this.$store.state.events.events || []
      if (!events.length) return events

      const { name } = this.$route

      if (name === 'organizationEvents') {
        return events.filter(
          ({ end, ongoing }) => isFuture(parseISO(end)) || ongoing
        )
      }

      if (name === 'myOrganizationEvents') {
        return events.filter(
          ({ user_id: userId, leaders, users }) =>
            userId === this.currentUserId ||
            !!leaders.find(({ id }) => id === this.currentUserId) ||
            !!users.find(({ id }) => id === this.currentUserId)
        )
      }

      if (name === 'pastOrganizationEvents') {
        return events.filter(({ end }) => isPast(parseISO(end)))
      }

      return events
    },
    reachedLastPage() {
      return this.maxEventsPages - this.eventsPage - 1 === 0
    },
    events() {
      const endIndex = (this.eventsPage + 1) * this.eventsPerPage
      return this.allEvents.slice(0, endIndex)
    },
    maxEventsPages() {
      return Math.ceil(this.allEvents.length / this.eventsPerPage) + 1
    },
    ...authComputed,
  },
  methods: {
    nextPage() {
      if (!this.reachedLastPage) {
        this.eventsPage++
      }
    },
    openEventModal(oneEvent) {
      this.singleEvent = oneEvent
      // HACK: show url for the event /organization/:organizationId/events/:eventId
      window.history.pushState(
        {},
        null,
        window.location.pathname.replace('/events', '') +
          '/events/' +
          oneEvent.id
      )
    },
    closeModal() {
      this.singleEvent = null
      window.history.back()
    },
    // Call only every 500ms so we don't update imediatelly if the user is browsing
    updateMonth: debounce(async function updateMonth(date) {
      this.currentDate = date
      await store.dispatch('events/setFilterMonth', {
        coHostOrganizationId: this.organizationId,
        monthDate: date,
      })
    }, 500),
    exportEvents() {
      const csvString = convertToCSV(this.allEvents)

      const fileName = `${this.$route.name}.csv`
      if (window.navigator.msSaveOrOpenBlob) {
        // IE
        const blob = new Blob([csvString])
        window.navigator.msSaveOrOpenBlob(blob, fileName)
      } else {
        const a = document.createElement('a')
        a.href = 'data:attachment/csv,' + encodeURIComponent(csvString)
        a.target = '_blank'
        a.download = fileName
        document.body.appendChild(a)
        a.click()
        setTimeout(() => a.remove(), 2000)
      }
    },
  },
}
</script>

<template>
  <Layout>
    <div
      v-infinite-scroll="nextPage"
      infinite-scroll-disabled="reachedLastPage"
    >
      <div class="bg-white">
        <div class="flex justify-between pl-10 pr-12 h-27 leading-27">
          <div class="">
            <div
              class="inline-block overflow-hidden align-middle bg-white rounded-full w-18 h-18 shadow-input"
            >
              <img v-if="organization.logo" :src="organization.logo" />
            </div>
            <div class="inline-block ml-5 align-middle">
              <h2 class="text-2xl text-blue-darker">{{ organization.name }}</h2>
            </div>
          </div>
          <div class="align-middle">
            <router-link
              class="inline-block w-48 h-12 mb-4 text-right button primary-button"
              :to="{
                name: 'organization',
                params: { organizationId: organizationId },
              }"
            >
              Back to Organization
            </router-link>
          </div>
        </div>

        <OrganizationEventsFilters :organization-id="organizationId" />

        <div class="flex justify-between pl-10 pr-12 mt-3 h-14 leading-14">
          <div class="flex">
            <BaseLink
              name="organizationEvents"
              class="events-tab"
              active-class="active"
              append
              exact
            >
              Discover
            </BaseLink>
            <BaseLink
              name="myOrganizationEvents"
              class="events-tab"
              active-class="active"
              append
            >
              My Events
            </BaseLink>
            <BaseLink
              name="pastOrganizationEvents"
              class="events-tab"
              active-class="active"
              append
            >
              Past
            </BaseLink>
          </div>
          <div class="align-middle">
            <button
              class="inline-block mb-4 text-base text-left button secondary-button"
              @click="exportEvents"
            >
              Export Event Details
            </button>
          </div>
        </div>
      </div>
      <RouterView :key="$route.fullPath" />
    </div>
  </Layout>
</template>
